import { Filter, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useGetIdentity, useGetList } from "ra-core";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ThemeToggle } from "../layout/ThemeToggle";
import { MeetingCard, MeetingCardSkeleton } from "./MeetingCard";
import { type Meeting } from "./meetingsUtils";

/**
 * MeetingsList — vista principal de Juntas.
 *
 * Comportamiento por rol:
 *  - Admin: toggle "Solo mis juntas" comienza OFF → ve todas las juntas
 *    de todos los vendedores. Puede activarlo para ver solo las suyas.
 *  - Non-admin: toggle comienza ON → ve solo sus juntas (las que capturó
 *    O donde fue agregado como participante). Puede apagarlo si quiere
 *    ver las de otros vendedores (RLS permite leer todo).
 *
 * El filtro "mías" usa el campo `sales_participant_ids` del view
 * meetings_summary, que ya incluye al primary sales_id + participants
 * extras (junction `meeting_sales_participants`).
 */
export function MeetingsList() {
  const [search, setSearch] = useState("");
  const { identity, isPending: identityPending } = useGetIdentity();

  // Default del toggle según rol del usuario.
  const isAdmin = (identity as any)?.administrator === true;
  const [onlyMine, setOnlyMine] = useState<boolean | null>(null);
  useEffect(() => {
    if (identityPending) return;
    if (onlyMine === null) setOnlyMine(!isAdmin);
  }, [identityPending, isAdmin, onlyMine]);

  // Filtro server-side: si onlyMine activo, filtra por sales_participant_ids.
  const { data: meetings, isPending, error } = useGetList<Meeting>(
    "meetings_summary",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "occurred_at", order: "DESC" },
      filter:
        onlyMine && identity?.id
          ? { "sales_participant_ids@cs": `{${identity.id}}` }
          : undefined,
    },
    { enabled: !identityPending && onlyMine !== null },
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return meetings ?? [];
    const q = search.toLowerCase().trim();
    return (meetings ?? []).filter((m) =>
      [
        m.company_name,
        m.deal_name,
        m.sales_name,
        m.title,
        m.location,
        m.topics,
        m.summary,
      ]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q)),
    );
  }, [meetings, search]);

  const loading = identityPending || isPending || onlyMine === null;
  const total = (meetings ?? []).length;
  const title = onlyMine ? "Mis juntas" : "Todas las juntas";

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header: título + toggle Solo mías + acciones */}
      <header className="px-7 pt-6 pb-4 flex items-center gap-3.5 flex-wrap">
        <div className="flex-1 min-w-0">
          <h1 className="m-0 text-[28px] font-bold tracking-[-0.025em]">
            {title}
          </h1>
          <div className="text-sm" style={{ color: "var(--musco-muted)" }}>
            {total === 0
              ? "Sin juntas todavía"
              : total === 1
              ? "1 junta registrada"
              : `${total} juntas registradas`}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Toggle Solo mías — admin lo tiene OFF por default, non-admin ON */}
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
            style={{
              background: "var(--musco-panel)",
              boxShadow: "var(--musco-shadow)",
            }}
          >
            <Switch
              id="meetings-only-mine"
              checked={!!onlyMine}
              onCheckedChange={(v) => setOnlyMine(v)}
              disabled={identityPending}
            />
            <Label
              htmlFor="meetings-only-mine"
              className="cursor-pointer text-sm whitespace-nowrap"
            >
              Solo mis juntas
            </Label>
          </div>
          <label className="musco-search">
            <Search size={14} aria-hidden />
            <input
              placeholder="Buscar junta, cliente, tema…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Buscar"
            />
          </label>
          <ThemeToggle />
          <button type="button" className="musco-btn musco-btn-g">
            <Filter size={14} /> Filtros
          </button>
          <Link
            to="/meetings/create"
            className="musco-btn musco-btn-p"
            aria-label="Nueva junta"
          >
            <Plus size={14} /> Nueva junta
          </Link>
        </div>
      </header>

      {/* Body — grid uniforme de cards */}
      <div className="flex-1 overflow-auto px-7 pb-7 pt-1">
        {error ? (
          <div
            className="rounded-2xl p-8 text-sm"
            style={{
              background: "var(--musco-panel)",
              color: "var(--musco-muted)",
              boxShadow: "var(--musco-shadow)",
            }}
          >
            Error al cargar las juntas:{" "}
            {String((error as Error).message ?? error)}
          </div>
        ) : loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MeetingCardSkeleton />
            <MeetingCardSkeleton />
            <MeetingCardSkeleton />
            <MeetingCardSkeleton />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState search={search} hasAny={total > 0} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((m) => (
              <MeetingCard key={m.id} meeting={m} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState({ search, hasAny }: { search: string; hasAny: boolean }) {
  if (search) {
    return (
      <div
        className="rounded-2xl p-12 text-center"
        style={{
          background: "var(--musco-panel)",
          color: "var(--musco-muted)",
          boxShadow: "var(--musco-shadow)",
        }}
      >
        <p className="text-base mb-1.5">
          No se encontraron juntas para "{search}"
        </p>
        <p className="text-sm">
          Prueba con otra palabra clave o quita el filtro.
        </p>
      </div>
    );
  }
  return (
    <div
      className="rounded-2xl p-12 text-center"
      style={{
        background: "var(--musco-panel)",
        boxShadow: "var(--musco-shadow)",
      }}
    >
      <h3
        className="m-0 mb-2 text-xl font-bold"
        style={{ letterSpacing: "-0.02em" }}
      >
        {hasAny
          ? "Aún no tienes juntas asignadas a ti"
          : "Aún no hay juntas capturadas"}
      </h3>
      <p
        className="mb-5 text-sm"
        style={{ color: "var(--musco-muted)", lineHeight: 1.55 }}
      >
        {hasAny
          ? "Aquí aparecerán las juntas que tú captures o donde te agreguen como participante."
          : "Conecta WhatsApp o Telegram al CRM para que cada audio de tu equipo comercial se transcriba y se guarde como junta — o crea una manualmente."}
      </p>
      <Link to="/meetings/create" className="musco-btn musco-btn-a">
        <Plus size={14} /> Capturar junta
      </Link>
    </div>
  );
}
